import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Removes the standard installation line item when an exclusive trigger product
 * is in the cart, so the customer can add the exclusive installation upsell.
 */
class CartInstallationUpsell {
  /** @type {boolean} */
  #isRunning = false;

  /** @type {Map<string, string[]>} */
  #productTagsCache = new Map();

  constructor() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /** @param {Event} event */
  #handleCartUpdate = (event) => {
    const source = event instanceof CustomEvent ? event.detail?.data?.source : undefined;
    if (source === 'cart-installation-upsell') return;

    this.#scheduleCheck();
  };

  #scheduleCheck() {
    window.clearTimeout(this.#timeoutId);
    this.#timeoutId = window.setTimeout(() => {
      this.#removeStandardInstallationIfNeeded();
    }, 150);
  }

  /** @type {number | undefined} */
  #timeoutId;

  async #removeStandardInstallationIfNeeded() {
    if (this.#isRunning) return;

    const config = this.#readConfig();
    if (!config?.standardVariantId) return;

    this.#isRunning = true;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      if (!response.ok) return;

      const cart = await response.json();
      const hasTrigger = await this.#cartHasExclusiveTrigger(cart, config);
      if (!hasTrigger) return;

      const standardLine = cart.items.find((/** @type {{ variant_id: number }} */ item) => {
        return item.variant_id === config.standardVariantId;
      });
      if (!standardLine) return;

      const line = cart.items.indexOf(standardLine) + 1;
      const sectionIds = this.#getSectionIds();
      if (!sectionIds.length) return;

      const body = JSON.stringify({
        line,
        quantity: 0,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      });

      const changeResponse = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      const parsed = await changeResponse.json();

      if (parsed.errors) {
        console.error(parsed.errors);
        return;
      }

      document.dispatchEvent(
        new CartUpdateEvent(parsed, 'cart-installation-upsell', {
          source: 'cart-installation-upsell',
          itemCount: parsed.item_count,
          sections: parsed.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.#isRunning = false;
    }
  }

  #readConfig() {
    const node = document.querySelector('[data-cart-installation-upsell-config]');
    if (!(node instanceof HTMLScriptElement) || !node.textContent) return null;

    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  }

  /**
   * @param {{ items: Array<{ product_id: number, handle: string }> }} cart
   * @param {{ triggerProductIds?: number[], exclusiveTriggerTag?: string }} config
   */
  async #cartHasExclusiveTrigger(cart, config) {
    const triggerIds = new Set(config.triggerProductIds || []);

    for (const item of cart.items) {
      if (triggerIds.has(item.product_id)) return true;
    }

    const tag = config.exclusiveTriggerTag?.trim().toLowerCase();
    if (!tag) return false;

    for (const item of cart.items) {
      if (triggerIds.has(item.product_id)) continue;

      const tags = await this.#getProductTags(item.handle);
      if (tags.some((productTag) => productTag.toLowerCase() === tag)) return true;
    }

    return false;
  }

  /** @param {string} handle */
  async #getProductTags(handle) {
    if (this.#productTagsCache.has(handle)) {
      return this.#productTagsCache.get(handle) || [];
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return [];

      const product = await response.json();
      const tags = Array.isArray(product.tags) ? product.tags : [];
      this.#productTagsCache.set(handle, tags);
      return tags;
    } catch {
      return [];
    }
  }

  #getSectionIds() {
    /** @type {string[]} */
    const sectionIds = [];

    document.querySelectorAll('cart-items-component[data-section-id]').forEach((element) => {
      if (element instanceof HTMLElement && element.dataset.sectionId) {
        sectionIds.push(element.dataset.sectionId);
      }
    });

    return sectionIds;
  }
}

new CartInstallationUpsell();
